import { Link, useParams, useNavigate } from "react-router-dom";

import { Fragment, useEffect, useState } from "react";
import { createPaymentIntent } from "../../services/payments/paymentPayout";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import FilingCheckout from "../payments/FilingCheckout";
import { StripePaymentIntentStatus } from "../../lib/Constants";
import NotificationAlert from "../ui/alerts/notifications/NotificationAlert";
import FilingAcceptedPayment from "../payments/accepted/FilingAcceptedPayment";
import FilingResume from "../payments/resumes/FilingResume";
import { updateFiling } from "../../api/filings/filingAPI";
import { Handler401 } from "../../api/utils/responseActions";
import "../../styles/v2/components/forms.css";

function TaxPriceForm({ filing, onPrev }) {
  const { filingUuid } = useParams();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentStatus, setPaymentIntentStatus] = useState();
  const [loading, setLoading] = useState(true);

  // Demo-specific states
  const [demoActivating, setDemoActivating] = useState(false);
  const [demoActivated, setDemoActivated] = useState(false);
  const [demoError, setDemoError] = useState(null);

  // Auto-redirect immediately when demo is activated
  useEffect(() => {
    if (demoActivated) {
      navigate(`/tax-filing/filing/${filing?.filing_uuid}?demo_welcome=true`);
    }
  }, [demoActivated, navigate, filingUuid, filing]);

  // Check if this is a demo filing
  const isDemo = filing?.mode === "DE";

  async function PaymentIntent() {
    setLoading(true);
    const url = "payments/payment-intent-filing";
    // Create PaymentIntent as soon as the page loads
    const response = await createPaymentIntent(url, filingUuid);

    if (response.status === 201) {
      const data = await response.json();
      setClientSecret(data.clientSecret);
      setPaymentIntentStatus(data.paymentIntentStatus);
      setLoading(false);
    } else {
      console.log("There is an error that need to be handled");
    }
  }

  useEffect(() => {
    // Skip payment intent for demo filings
    if (isDemo) {
      setLoading(false);
      return;
    }
    PaymentIntent();
  }, [isDemo]);

  const handleStartDemo = async () => {
    setDemoActivating(true);
    setDemoError(null);

    try {
      const apiUrl = `tax/filing`;
      const response = await updateFiling(
        apiUrl,
        { payment_accepted: true },
        filingUuid
      );

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      if (response.ok) {
        setDemoActivated(true);
      } else {
        setDemoError("Failed to start demo. Please try again.");
      }
    } catch (error) {
      console.error("Error starting demo:", error);
      setDemoError("An unexpected error occurred. Please try again.");
    } finally {
      setDemoActivating(false);
    }
  };

  // Demo filing UI - single centered column
  if (isDemo) {
    // Redirect happens immediately via useEffect when demoActivated is true
    // Show loading state briefly while redirecting
    if (demoActivated) {
      return <LoadingPlaceholder rows={1} type="card-large" />;
    }

    return (
      <>
        {loading && <LoadingPlaceholder rows={1} type="card-large" />}
        {!loading && (
          <div className="columns">
            <div className="tax-price-demo-container">
              <FilingResume
                filing={filing}
                isConfirmed={true}
                hideConfirmation={true}
              />

              {demoError && (
                <div className="tax-price-demo-error">
                  {demoError}
                </div>
              )}

              <div className="tax-price-demo-button-container">
                <button
                  className="cta-button primary tax-price-demo-button-full-width"
                  onClick={handleStartDemo}
                  disabled={demoActivating}
                >
                  {demoActivating ? "Starting Demo..." : "Start Demo"}
                </button>
              </div>
              <div className="prevNext tax-price-demo-back-container">
                <button
                  className="cta-button secondary tax-price-demo-back-button"
                  onClick={onPrev}
                  disabled={demoActivating}
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Regular filing UI - two columns with payment
  return (
    <>
      {loading && <LoadingPlaceholder rows={1} type="card-large" />}
      {!loading && (
        <div className="columns">
          {paymentIntentStatus ===
            StripePaymentIntentStatus.REQUIRES_PAYMENT_METHOD && (
            <Fragment>
              <FilingCheckout
                clientSecret={clientSecret}
                filing={filing}
                filingUuid={filingUuid}
              />
              <div className="prevNext">
                <button
                  className="cta-button secondary tax-price-back-button"
                  onClick={onPrev}
                >
                  Back
                </button>
              </div>
            </Fragment>
          )}
          {paymentIntentStatus === StripePaymentIntentStatus.SUCCEEDED && (
            <FilingAcceptedPayment filingUuid={filingUuid} />
          )}
          {paymentIntentStatus === StripePaymentIntentStatus.PROCESSING && (
            <NotificationAlert
              alertType="info"
              alertTitle="Payment Processing"
              alertDescription="The payment is being processed by stripe"
            />
          )}
          {paymentIntentStatus === StripePaymentIntentStatus.CANCELED && (
            <NotificationAlert
              alertType="error"
              alertTitle="Payment Fails"
              alertDescription="Payment Failed. Please contact us!"
            />
          )}
        </div>
      )}
    </>
  );
}

export default TaxPriceForm;
